import React, { useState, useEffect } from 'react';
import { 
  MoreVertical, 
  Eye, 
  Edit, 
  Shield, 
  Ban,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  Wallet,
  Award,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
  Users,
  Search,
  X
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const UserTable = ({ 
  users, 
  pagination,
  onView, 
  onEdit, 
  onRefresh,
  onPageChange,
  onSearch,
  searchTerm = '',
  isLoading = false 
}) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(7);
  const [itemsPerPage, setItemsPerPage] = useState(pagination?.limit || 10);
  
  // Local search state with debounce
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const { updateUserRole, banUser, unbanUser, suspendUser, unsuspendUser } = useAdminStore();

  // Sync with parent search term
  useEffect(() => {
    if (searchTerm !== localSearchTerm) {
      setLocalSearchTerm(searchTerm || '');
    }
  }, [searchTerm]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (onSearch) {
        onSearch(value);
      }
    }, 500);
    
    setSearchTimeout(timeout);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(localSearchTerm);
    }
  };

  const handleRoleUpdate = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setSelectedUserId(null);
      setShowActions(false);
      toast.success(`User role updated to ${newRole}`);
    } catch (error) {
      console.error('Update role error:', error);
    }
  };

  const handleBan = async () => {
    if (!banReason.trim()) {
      toast.error('Please provide a reason for banning');
      return;
    }

    try {
      await banUser(selectedUserId, banReason);
      setShowBanModal(false);
      setBanReason('');
      setSelectedUserId(null);
      setShowActions(false);
      if (onRefresh) onRefresh();
      toast.success('User banned successfully');
    } catch (error) {
      console.error('Ban user error:', error);
    }
  };

  const handleUnban = async (userId) => {
    if (window.confirm('Are you sure you want to unban this user?')) {
      try {
        await unbanUser(userId);
        setSelectedUserId(null);
        setShowActions(false);
        if (onRefresh) onRefresh();
        toast.success('User unbanned successfully');
      } catch (error) {
        console.error('Unban user error:', error);
      }
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error('Please provide a reason for suspension');
      return;
    }

    try {
      await suspendUser(selectedUserId, suspendReason, suspendDays);
      setShowSuspendModal(false);
      setSuspendReason('');
      setSuspendDays(7);
      setSelectedUserId(null);
      setShowActions(false);
      if (onRefresh) onRefresh();
      toast.success(`User suspended for ${suspendDays} days`);
    } catch (error) {
      console.error('Suspend user error:', error);
    }
  };

  const handleUnsuspend = async (userId) => {
    if (window.confirm('Are you sure you want to unsuspend this user?')) {
      try {
        await unsuspendUser(userId);
        setSelectedUserId(null);
        setShowActions(false);
        if (onRefresh) onRefresh();
        toast.success('User unsuspended successfully');
      } catch (error) {
        console.error('Unsuspend user error:', error);
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (onPageChange && newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      onPageChange(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    if (onPageChange) {
      onPageChange(1, newLimit);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      ADMIN: { color: 'bg-red-50 text-red-700 border-red-200', label: 'Admin', icon: Shield },
      RESELLER: { color: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Reseller', icon: Award },
      USER: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'User', icon: UserCheck },
    };
    
    const config = roleConfig[role] || roleConfig.USER;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${config.color}`}>
        <Icon className="h-2.5 w-2.5 mr-0.5" />
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (user) => {
    if (user.isBanned) {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 border border-red-200">
          <Ban className="h-2.5 w-2.5 mr-0.5" />
          Banned
        </span>
      );
    }
    if (user.isSuspended) {
      const endDate = user.suspensionEnd ? new Date(user.suspensionEnd).toLocaleDateString() : 'N/A';
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
          <Clock className="h-2.5 w-2.5 mr-0.5" />
          Suspended
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200">
        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
        Active
      </span>
    );
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const total = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = (currentPage - 1) * (pagination?.limit || 10) + 1;
  const endIndex = Math.min(currentPage * (pagination?.limit || 10), total);

  return (
    <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
      {/* Search Bar */}
      <div className="px-3 py-2 border-b border-primary-100/50 bg-gray-50/50">
        <div className="flex flex-col sm:flex-row items-center gap-2">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name, email, phone, or ID..."
              value={localSearchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-8 pr-8 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
            />
            {localSearchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center space-x-1.5 flex-shrink-0">
            <span className="text-[10px] text-gray-500">
              {total > 0 ? `${total} users found` : 'No users found'}
            </span>
            {isLoading && (
              <RefreshCw className="h-3.5 w-3.5 text-primary-500 animate-spin" />
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-primary-100/50 text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Verified</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Balance</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-primary-100/50">
            {isLoading ? (
              <tr>
                <td colSpan="8" className="px-3 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <RefreshCw className="h-4 w-4 text-primary-500 animate-spin" />
                    <span className="text-xs text-gray-500">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-3 py-8 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 shadow-neomorph-inset mb-3">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 font-medium">No users found</p>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/30 flex items-center justify-center flex-shrink-0">
                        {user.avatar ? (
                          <img 
                            src={user.avatar} 
                            alt={user.firstName}
                            className="h-8 w-8 rounded-lg object-cover"
                          />
                        ) : (
                          <span className="text-white font-semibold text-[10px]">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </span>
                        )}
                      </div>
                      <div className="ml-2.5">
                        <div className="text-xs font-medium text-gray-800">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-[10px] text-gray-500 flex items-center mt-0.5">
                          <Mail className="h-2.5 w-2.5 mr-0.5" />
                          {user.email}
                        </div>
                        <div className="text-[10px] text-gray-500 flex items-center">
                          <Phone className="h-2.5 w-2.5 mr-0.5" />
                          {user.phone}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getStatusBadge(user)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {getRoleBadge(user.role)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {user.phoneVerified ? (
                      <span className="inline-flex items-center text-green-600 bg-green-50 px-1.5 py-0.5 rounded text-[10px]">
                        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-red-600 bg-red-50 px-1.5 py-0.5 rounded text-[10px]">
                        <XCircle className="h-2.5 w-2.5 mr-0.5" />
                        No
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center text-xs font-semibold text-gray-800 bg-green-50 px-2 py-0.5 rounded">
                      <Wallet className="h-2.5 w-2.5 text-green-600 mr-0.5" />
                      {formatCurrency(user.wallet?.balance || 0)}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-2.5 w-2.5 mr-0.5 text-gray-400" />
                      {formatDate(user.createdAt)}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                    {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap relative">
                    <button
                      onClick={() => {
                        setSelectedUserId(user.id);
                        setShowActions(!showActions);
                      }}
                      className="p-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
                    >
                      <MoreVertical className="h-3.5 w-3.5 text-gray-600" />
                    </button>
                    
                    {selectedUserId === user.id && showActions && (
                      <div className="absolute right-0 mt-1 w-48 bg-[#E8ECF5] rounded-xl shadow-neomorph border border-primary-100/30 z-10 overflow-hidden">
                        <button
                          onClick={() => {
                            onView(user);
                            setShowActions(false);
                            setSelectedUserId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-white transition-all"
                        >
                          <div className="p-1 bg-blue-50 rounded-lg mr-2">
                            <Eye className="h-3 w-3 text-blue-600" />
                          </div>
                          View
                        </button>
                        
                        <button
                          onClick={() => {
                            onEdit(user);
                            setShowActions(false);
                            setSelectedUserId(null);
                          }}
                          className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-white transition-all"
                        >
                          <div className="p-1 bg-green-50 rounded-lg mr-2">
                            <Edit className="h-3 w-3 text-green-600" />
                          </div>
                          Edit
                        </button>
                        
                        <div className="border-t border-primary-100/50">
                          <button
                            onClick={() => handleRoleUpdate(user.id, 'ADMIN')}
                            disabled={user.role === 'ADMIN' || isLoading}
                            className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-white transition-all disabled:opacity-50"
                          >
                            <div className="p-1 bg-red-50 rounded-lg mr-2">
                              <Shield className="h-3 w-3 text-red-600" />
                            </div>
                            Make Admin
                          </button>
                          <button
                            onClick={() => handleRoleUpdate(user.id, 'RESELLER')}
                            disabled={user.role === 'RESELLER' || isLoading}
                            className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-white transition-all disabled:opacity-50"
                          >
                            <div className="p-1 bg-purple-50 rounded-lg mr-2">
                              <Award className="h-3 w-3 text-purple-600" />
                            </div>
                            Make Reseller
                          </button>
                          <button
                            onClick={() => handleRoleUpdate(user.id, 'USER')}
                            disabled={user.role === 'USER' || isLoading}
                            className="flex items-center w-full px-3 py-2 text-xs text-gray-700 hover:bg-white transition-all disabled:opacity-50"
                          >
                            <div className="p-1 bg-blue-50 rounded-lg mr-2">
                              <UserCheck className="h-3 w-3 text-blue-600" />
                            </div>
                            Make User
                          </button>
                        </div>
                        
                        <div className="border-t border-primary-100/50">
                          {user.isSuspended ? (
                            <button
                              onClick={() => {
                                handleUnsuspend(user.id);
                                setShowActions(false);
                                setSelectedUserId(null);
                              }}
                              className="flex items-center w-full px-3 py-2 text-xs text-green-600 hover:bg-white transition-all"
                            >
                              <div className="p-1 bg-green-50 rounded-lg mr-2">
                                <UserCheck className="h-3 w-3 text-green-600" />
                              </div>
                              Unsuspend
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedUserId(user.id);
                                setShowSuspendModal(true);
                                setShowActions(false);
                              }}
                              className="flex items-center w-full px-3 py-2 text-xs text-yellow-600 hover:bg-white transition-all"
                            >
                              <div className="p-1 bg-yellow-50 rounded-lg mr-2">
                                <Clock className="h-3 w-3 text-yellow-600" />
                              </div>
                              Suspend
                            </button>
                          )}
                        </div>
                        
                        <div className="border-t border-primary-100/50">
                          {user.isBanned ? (
                            <button
                              onClick={() => {
                                handleUnban(user.id);
                                setShowActions(false);
                                setSelectedUserId(null);
                              }}
                              className="flex items-center w-full px-3 py-2 text-xs text-green-600 hover:bg-white transition-all"
                            >
                              <div className="p-1 bg-green-50 rounded-lg mr-2">
                                <UserCheck className="h-3 w-3 text-green-600" />
                              </div>
                              Unban
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedUserId(user.id);
                                setShowBanModal(true);
                                setShowActions(false);
                              }}
                              className="flex items-center w-full px-3 py-2 text-xs text-red-600 hover:bg-white transition-all"
                            >
                              <div className="p-1 bg-red-50 rounded-lg mr-2">
                                <Ban className="h-3 w-3 text-red-600" />
                              </div>
                              Ban
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {!isLoading && users.length > 0 && (
        <div className="px-3 py-2 border-t border-primary-100/50 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center space-x-1.5">
              <label className="text-[10px] text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-0.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-[10px]"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-[10px] text-gray-500">per page</span>
            </div>

            <div className="text-[10px] text-gray-600">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span> users
            </div>

            <div className="flex items-center space-x-0.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-6 h-6 rounded-lg text-[10px] font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban Modal */}
      {showBanModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => setShowBanModal(false)}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl bg-red-50 shadow-neomorph-inset sm:mx-0 sm:h-8 sm:w-8">
                  <Ban className="h-5 w-5 text-red-600" />
                </div>
                <div className="mt-2 text-center sm:mt-0 sm:ml-3 sm:text-left w-full">
                  <h3 className="text-base font-semibold text-gray-800">Ban User</h3>
                  <div className="mt-1">
                    <p className="text-xs text-gray-500">This action cannot be undone.</p>
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Enter reason for banning..."
                  className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs min-h-[80px] resize-none"
                  maxLength={500}
                />
                <p className="mt-0.5 text-[10px] text-gray-500 text-right">{banReason.length}/500</p>
              </div>

              <div className="mt-4 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleBan}
                  disabled={isLoading || !banReason.trim()}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 text-xs font-medium text-white shadow-md shadow-red-500/30 hover:shadow-lg focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {isLoading ? 'Banning...' : 'Ban'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBanModal(false);
                    setBanReason('');
                  }}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-200 px-3 py-1.5 bg-white text-xs font-medium text-gray-700 shadow-neomorph hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => setShowSuspendModal(false)}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl bg-yellow-50 shadow-neomorph-inset sm:mx-0 sm:h-8 sm:w-8">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="mt-2 text-center sm:mt-0 sm:ml-3 sm:text-left w-full">
                  <h3 className="text-base font-semibold text-gray-800">Suspend User</h3>
                  <div className="mt-1">
                    <p className="text-xs text-gray-500">Suspend user for violating guidelines.</p>
                  </div>
                </div>
              </div>

              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Duration</label>
                  <select
                    value={suspendDays}
                    onChange={(e) => setSuspendDays(parseInt(e.target.value))}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Reason</label>
                  <textarea
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    placeholder="Enter reason for suspension..."
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs min-h-[80px] resize-none"
                    maxLength={500}
                  />
                  <p className="mt-0.5 text-[10px] text-gray-500 text-right">{suspendReason.length}/500</p>
                </div>
              </div>

              <div className="mt-4 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSuspend}
                  disabled={isLoading || !suspendReason.trim()}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent px-3 py-1.5 bg-gradient-to-r from-yellow-600 to-yellow-700 text-xs font-medium text-white shadow-md shadow-yellow-500/30 hover:shadow-lg focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {isLoading ? 'Suspending...' : 'Suspend'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSuspendModal(false);
                    setSuspendReason('');
                    setSuspendDays(7);
                  }}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-200 px-3 py-1.5 bg-white text-xs font-medium text-gray-700 shadow-neomorph hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;